import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { FilterMatchMode } from "primereact/api";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import axios from "axios";

import {
    SERVICE_LOCATION, GET_SAGE_EXPORT_BATCH_DATA_BETWEEN_DATES
} from "../constants/endpoints";

import "./../assets/styles/table.css";
import "./../assets/styles/worksheets.css";

const SagePayappExport = (props) => {
    const toast = useRef(null);

    const { data } = props;

    const [loading, setLoading] = useState(false);
    const [sitesData, setSitesData] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [minToDate, setMinToDate] = useState(null);
    const [disableGoButton, setDisableGoButton] = useState(false);
    const [selectedSiteData, setSelectedSiteData] = useState(null);

    const [siteDataPage, setSiteDataPage] = useState(0);

    useEffect(() => {
        const currentDate = new Date();
        const previousDate = new Date(currentDate);
        previousDate.setDate(currentDate.getDate() - 2);
        setFromDate(previousDate);
        setToDate(currentDate);
        getSitesData(previousDate, currentDate);
    }, [])

    const getFormattedDate = (dateToFormat) => {
        const year = dateToFormat.getFullYear();
        const month = String(dateToFormat.getMonth() + 1).padStart(2, "0");
        const day = String(dateToFormat.getDate()).padStart(2, "0");
        const formatedDate = `${year}-${month}-${day}`;
        return formatedDate;
    }

    const handleGo = () => {
        getSitesData(fromDate, toDate);
    }

    const handleFromDate = (e) => {
        const dateSelected = e.value;
        setFromDate(dateSelected);
        setMinToDate(dateSelected);
        setToDate(null);
        setDisableGoButton(true);
    }

    const handleToDate = (e) => {
        const dateSelected = e.value;
        setToDate(dateSelected);
        setDisableGoButton(false);
    }

    const handleSelectedSiteData = (e) => {
        setSelectedSiteData(e.value);
    }

    const getSitesData = (selectedFromDate, selectedToDate) => {
        setLoading(true);
        const endPoint = SERVICE_LOCATION + GET_SAGE_EXPORT_BATCH_DATA_BETWEEN_DATES;
        const formatedFromDate = getFormattedDate(selectedFromDate);
        const formatedToDate = getFormattedDate(selectedToDate)
        const requestedData = {
            startDate: formatedFromDate,
            endDate: formatedToDate
        }
        axios.post(endPoint, requestedData, {
            headers: {
                Authorization: `Bearer ${data.data.token}`,
            },
        }).then((response) => {
            if (response.data.isSuccess) {
                const processedData = response.data.data.sagePayappExportData;
                setSitesData(processedData);
            }
            else if (!response.data.isSuccess) {
                setSitesData(null);
            }
            setLoading(false);
            clearFilter();
        }).catch((error) => {
            setLoading(false)
            toast.current.show({ severity: "error", summary: "Error", detail: error, life: 3000 });
        })
    }

    //export functionality
    const saveAsExcelFile = (buffer, fileName) => {
        import("file-saver").then((module) => {
            if (module && module.default) {
                let EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
                let EXCEL_EXTENSION = ".xlsx";
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE
                });

                module.default.saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    const exportExcel = () => {
        const exportData = selectedSiteData == null || selectedSiteData.length === 0 ? sitesData : selectedSiteData

        const excludedColumns = ["exportId", "sageExportBatch"];

        const columnHeaders = {
            type: "Type",
            accountReference: "Account Reference",
            nominalCode: "Nominal Code",
            departmentCode: "Department Code",
            date: "Date",
            reference: "Reference",
            details: "Details",
            netAmount: "Net Amount",
            taxCode: "Tax Code",
            taxAmount: "Tax Amount",
            exchangeRate: "Exchange Rate",
            extraReference: "Extra Reference",
            userName: "User Name",
            projectRef: "Project Ref",
            costCodeRef: "Cost Code Ref"
        };

        const transformedData = exportData.map((item) => {
            let newItem = {};
            Object.keys(item).forEach((key) => {
                if (!excludedColumns.includes(key)) {
                    newItem[columnHeaders[key] || key] = item[key];
                }
            });
            return newItem;
        });

        import("xlsx").then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(transformedData);
            const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
            const excelBuffer = xlsx.write(workbook, {
                bookType: "xlsx",
                type: "array"
            });

            saveAsExcelFile(excelBuffer, "SageBatchExport");
        });
    };
    //end export functionality

    // Site search functionality
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [globalFilterValue, setGlobalFilterValue] = useState("");

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters["global"].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const clearFilter = () => {
        setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
        setGlobalFilterValue("");
    };

    const header = () => {
        return (
            <div className="row">
                <div className="col-12 col-sm-2 col-md-2 col-lg-2 col-xl-2">
                    <label htmlFor="from-date">From</label>
                    <Calendar id="from-date" name="from-date" value={fromDate} onChange={handleFromDate} showIcon dateFormat="dd/mm/yy" />
                </div>
                <div className="col-12 col-sm-2 col-md-2 col-lg-2 col-xl-2">
                    <label htmlFor="to-date">To</label>
                    <Calendar id="to-date" name="to-date" value={toDate} onChange={handleToDate} minDate={minToDate} showIcon dateFormat="dd/mm/yy" />
                </div>
                <div className="col-12 col-sm-1 col-md-1 col-lg-1 col-xl-1 padt-19">
                    <Button id="go" label="Go" disabled={disableGoButton} onClick={handleGo} />
                </div>
                <div className="col-12 col-sm-7 col-md-7 col-lg-7 col-xl-7 padt-15">
                    <div className="flex justify-content-end txt-right">
                        <Button className="mar-r-10" type="button" icon="pi pi-file-excel" severity="success" onClick={exportExcel} rounded tooltip="Export Excel Report" />
                        <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} tooltip="clear filters" tooltipOptions={{ position: "bottom" }} />
                        <span className="p-input-icon-left marl-20">
                            <i className="pi pi-search" />
                            <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                        </span>
                    </div>
                </div>
            </div>
        );
    };
    // Search site functionality Ends here

    const sitesTablePageChange = (event) => {
        setSiteDataPage(event.first / event.rows);
    };

    return (
        <div className={`app ${loading ? "disabled" : ""}`}>
            {
                loading &&
                <div className="spinner-overlay">
                    <ProgressSpinner style={{ width: "50px", height: "50px" }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
                </div>
            }
            <Toast ref={toast} />

            <div className="worksheets-section">
                <h4>Sage Export Batch</h4>
                <DataTable className="font-12" value={sitesData} datakey="exportId" paginator rows={10} first={siteDataPage * 10}
                    onPage={sitesTablePageChange} metaKeySelection={false} filters={filters} header={header}
                    selection={selectedSiteData} onSelectionChange={handleSelectedSiteData}
                    globalFilterFields={["exportId", "sageExportBatch", "accountReference", "type", "nominalCode", "departmentCode", "date", "reference",
                        "details", "netAmount", "taxCode", "taxAmount", "exchangeRate", "extraReference", "userName", "projectRef", "costCodeRef"
                    ]} scrollable tableStyle={{ minWidth: "150rem" }} emptyMessage="No export payapps available.">
                    <Column selectionMode="multiple" headerStyle={{ width: "3rem" }}></Column>
                    <Column field="exportId" header="Export Id" hidden></Column>
                    <Column field="sageExportBatch" header="Sage Export Batch"></Column>
                    <Column field="accountReference" header="Account Reference"></Column>
                    <Column field="type" header="Type"></Column>
                    <Column field="nominalCode" header="Nominal Code"></Column>
                    <Column field="departmentCode" header="Department Code"></Column>
                    <Column field="date" header="Date"></Column>
                    <Column field="reference" header="Reference"></Column>
                    <Column field="details" header="Details"></Column>
                    <Column field="netAmount" header="Net Amount"></Column>
                    <Column field="taxCode" header="Tax Code"></Column>
                    <Column field="taxAmount" header="Tax Amount"></Column>
                    <Column field="exchangeRate" header="Exchange Rate"></Column>
                    <Column field="extraReference" header="Extra Reference"></Column>
                    <Column field="userName" header="User Name"></Column>
                    <Column field="projectRef" header="Project Ref"></Column>
                    <Column field="costCodeRef" header="Cost Code Ref"></Column>
                </DataTable>
            </div>
        </div>
    )
}

export default SagePayappExport